import type { FilingObligationDto } from '@signalvote/shared';

/**
 * How long is left on a deadline that is measured in HOURS, not days.
 *
 * A 48-hour notice (federal 11 CFR 104.5(f), NC §163-278.9(a)(2)) lands mid-day, and the obligation
 * list's `daysRemaining` is a whole-day difference — so a notice due in 40 hours and one due in 3
 * both read "2 days". That is the difference between "get to it" and "drop everything", and the
 * queue card is the only place the treasurer sees it.
 *
 * Rounds DOWN at every step, so the label never flatters the deadline: 59 minutes out reads
 * "59 minutes left", not "1 hour left". Returns null when the deadline has passed (an overdue card
 * says how LATE it is instead) or when there is no deadline instant to count down to.
 */
export function timeRemainingLabel(deadlineUtc: string | null, now: Date = new Date()): string | null {
  if (!deadlineUtc) return null;

  const deadline = new Date(deadlineUtc).getTime();
  if (Number.isNaN(deadline)) return null;

  const msLeft = deadline - now.getTime();
  if (msLeft <= 0) return null;

  const minutes = Math.floor(msLeft / 60_000);
  if (minutes < 60) return plural(minutes, 'minute');

  const hours = Math.floor(minutes / 60);
  if (hours < 48) return plural(hours, 'hour');

  return plural(Math.floor(hours / 24), 'day');
}

function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'} left`;
}

const DAY_MS = 86_400_000;

/**
 * "48-hour notice" (federal, NC, MS) or New Jersey's "72-hour notice" / "24-hour notice". A notice
 * is due its hours after 12:00 a.m. campaign time on the receipt date, and a US campaign's midnight
 * falls 4 to 10 hours after UTC midnight, so the whole days between the receipt date and the
 * deadline recover the hours.
 */
export function noticeLabel(o: Pick<FilingObligationDto, 'contributionDate' | 'deadlineUtc'>): string {
  if (!o.contributionDate || !o.deadlineUtc) return 'pre-election notice';
  const days = Math.floor((Date.parse(o.deadlineUtc) - Date.parse(`${o.contributionDate}T00:00:00Z`)) / DAY_MS);
  return Number.isNaN(days) ? 'pre-election notice' : `${days * 24}-hour notice`;
}
